import { Controller } from 'react-hook-form';
import { Box, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../shared/components/FormDialog';
import { ColaboradorSelect } from '../../shared/components/ColaboradorSelect';
import { ClienteSelect } from '../../shared/components/ClienteSelect';
import { ProjetoSelect } from '../../shared/components/ProjetoSelect';
import { TipoTarefaSelect } from '../../shared/components/TipoTarefaSelect';
import { TipoHoraSelect } from '../../shared/components/TipoHoraSelect';
import { DatePickerPtBr } from '../../shared/components/DatePickerPtBr';
import { useCrudFormDialog } from '../../shared/api/useCrudFormDialog';
import { useAuth } from '../../shared/auth/useAuth';
import { timesheetEntriesHooks } from './hooks';
import { timesheetEntryInputSchema, type TimesheetEntryFormValues } from './schemas';
import type { TimesheetEntryDto, TimesheetEntryInput } from './types';

export interface TimesheetEntryFormDialogProps {
  open: boolean;
  entry: TimesheetEntryDto | null;
  onClose: () => void;
}

function buildDefaultValues(lockedColabId: number | null): TimesheetEntryFormValues {
  return {
    colaboradorCodigo: lockedColabId ?? 0,
    clienteCodigo: 0,
    clienteProjetoCodigo: 0,
    tipoTarefaCodigo: 0,
    tipoHoraCodigo: 0,
    data: '',
    horas: '',
    atividade: '',
    codigoExterno: null,
  };
}

/** Aplica a máscara HH:MM enquanto o usuário digita (só dígitos, max 4). */
function maskHoras(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

export function TimesheetEntryFormDialog({
  open,
  entry,
  onClose,
}: TimesheetEntryFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isColab = user?.perfil === 'COLABORADOR';
  const lockedColabId = isColab ? (user?.externalId ?? null) : null;

  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    TimesheetEntryFormValues,
    TimesheetEntryDto,
    TimesheetEntryInput,
    string
  >({
    open,
    entity: entry,
    schema: timesheetEntryInputSchema,
    defaultValues: buildDefaultValues(lockedColabId),
    fromEntity: (e) => ({
      colaboradorCodigo: e.colaboradorCodigo,
      clienteCodigo: e.clienteCodigo,
      clienteProjetoCodigo: e.clienteProjetoCodigo,
      tipoTarefaCodigo: e.tipoTarefaCodigo,
      tipoHoraCodigo: e.tipoHoraCodigo,
      data: e.data,
      horas: e.horas,
      atividade: e.atividade,
      codigoExterno: e.codigoExterno,
    }),
    getId: (e) => e.id,
    hooks: {
      useCreate: timesheetEntriesHooks.useCreate,
      useUpdate: timesheetEntriesHooks.useUpdate,
    },
    messages: {
      created: t('features.apontamentos.toasts.created'),
      updated: t('features.apontamentos.toasts.updated'),
    },
    onClose,
  });

  return (
    <FormDialog
      open={open}
      title={isEdit ? t('features.apontamentos.editTitle') : t('features.apontamentos.createTitle')}
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
      maxWidth="md"
    >
      <Stack spacing={2}>
        {/* FKs compõem a PK — disabled em edit per spec § 6 (mudar requer Delete + Create). */}
        <Controller
          control={form.control}
          name="colaboradorCodigo"
          render={({ field, fieldState }) => (
            <ColaboradorSelect
              value={field.value || null}
              onChange={(id) => field.onChange(id ?? 0)}
              label={t('features.apontamentos.fields.colaborador')}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              disabled={isEdit || isColab}
            />
          )}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Controller
              control={form.control}
              name="clienteCodigo"
              render={({ field, fieldState }) => (
                <ClienteSelect
                  value={field.value || null}
                  onChange={(id) => {
                    const prev = form.getValues('clienteCodigo');
                    field.onChange(id ?? 0);
                    // Cascata: trocou de cliente → zera projeto (compara contra valor anterior).
                    if (id !== prev) {
                      form.setValue('clienteProjetoCodigo', 0, { shouldValidate: false });
                    }
                  }}
                  label={t('features.apontamentos.fields.cliente')}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  disabled={isEdit}
                  apontavelParaColaborador={lockedColabId}
                />
              )}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Controller
              control={form.control}
              name="clienteProjetoCodigo"
              render={({ field, fieldState }) => (
                <ProjetoSelect
                  value={field.value || null}
                  onChange={(id) => field.onChange(id ?? 0)}
                  clienteId={form.watch('clienteCodigo') || null}
                  label={t('features.apontamentos.fields.projeto')}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  disabled={isEdit || !form.watch('clienteCodigo')}
                  apontavelParaColaborador={lockedColabId}
                />
              )}
            />
          </Box>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Controller
              control={form.control}
              name="tipoTarefaCodigo"
              render={({ field, fieldState }) => (
                <TipoTarefaSelect
                  value={field.value || null}
                  onChange={(id) => field.onChange(id ?? 0)}
                  label={t('features.apontamentos.fields.tipoTarefa')}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Controller
              control={form.control}
              name="tipoHoraCodigo"
              render={({ field, fieldState }) => (
                <TipoHoraSelect
                  value={field.value || null}
                  onChange={(id) => field.onChange(id ?? 0)}
                  label={t('features.apontamentos.fields.tipoHora')}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Box>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Controller
              control={form.control}
              name="data"
              render={({ field, fieldState }) => (
                <DatePickerPtBr
                  value={field.value || null}
                  onChange={(iso) => field.onChange(iso ?? '')}
                  label={t('features.apontamentos.fields.data')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(fieldState.error),
                      helperText: fieldState.error?.message,
                    },
                  }}
                />
              )}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Controller
              control={form.control}
              name="horas"
              render={({ field, fieldState }) => (
                <TextField
                  label={t('features.apontamentos.fields.horas')}
                  fullWidth
                  value={field.value}
                  onChange={(e) => field.onChange(maskHoras(e.target.value))}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  inputProps={{ inputMode: 'numeric', maxLength: 5, placeholder: 'HH:MM' }}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ?? t('features.apontamentos.fields.horasHelper')
                  }
                />
              )}
            />
          </Box>
        </Stack>

        <TextField
          label={t('features.apontamentos.fields.atividade')}
          fullWidth
          multiline
          minRows={3}
          inputProps={{ maxLength: 2000 }}
          error={Boolean(form.formState.errors.atividade)}
          helperText={form.formState.errors.atividade?.message}
          {...form.register('atividade')}
        />

        <Controller
          control={form.control}
          name="codigoExterno"
          render={({ field, fieldState }) => (
            <TextField
              label={t('features.apontamentos.fields.codigoExterno')}
              fullWidth
              inputProps={{ maxLength: 60 }}
              error={Boolean(fieldState.error)}
              helperText={
                fieldState.error?.message ?? t('features.apontamentos.fields.codigoExternoHelper')
              }
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
              onBlur={field.onBlur}
              inputRef={field.ref}
            />
          )}
        />
      </Stack>
    </FormDialog>
  );
}
